// KPIs: paid seats against the goal, hours per seat, seat retention and bugs per week, with the numbers behind
// them (design §13). Seats and hours are typed in here; the rest is computed from what the platform already keeps.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { KpiGoals, KpiMeta, KpiResponse } from '@upm/domain';
import { api, ApiError } from '../../lib/api';
import { firstName, fmtCalendarDate } from '../../lib/format';
import { Banner, Button, Pill, ProgressBar, Spinner, TableBox, Td, Th, cx, type PillTone } from '../../ui';
import { BugsChart, SeatsChart } from './Charts';
import { GoalsDialog, LogHoursDialog, SeatChangeDialog } from './Dialogs';

type Weeks = 12 | 26;
type Window = 'all' | '90';

const dayLabel = (on: string) => { const [y, m, d] = on.split('-').map(Number); return new Date(y!, m! - 1, d!).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }); };
const short = (on: string) => fmtCalendarDate(on).replace(/, \d{4}$/, '');
const n1 = (v: number | null, unit = '') => (v === null ? '—' : `${v}${unit}`);

export function KpisPage() {
  const [data, setData] = useState<KpiResponse | null>(null);
  const [meta, setMeta] = useState<KpiMeta['firms']>([]);
  const [error, setError] = useState<string | null>(null);
  const [weeks, setWeeks] = useState<Weeks>(12);
  const [window, setWindow] = useState<Window>('all');
  const [dialog, setDialog] = useState<'seat' | 'hours' | 'goals' | null>(null);

  const load = useCallback(async () => {
    try { setData(await api.get<KpiResponse>(`/api/admin/kpis?weeks=${weeks}&hours=${window}`)); setError(null); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not load the KPIs.'); }
  }, [weeks, window]);
  useEffect(() => { void load(); }, [load]);
  // The firms for the entry dialogs; reloaded after each save so a new firm shows up.
  const loadMeta = useCallback(async () => { try { setMeta((await api.get<KpiMeta>('/api/admin/kpis/meta')).firms); } catch { /* the dialogs show an empty list */ } }, []);
  useEffect(() => { void loadMeta(); }, [loadMeta]);
  const saved = useCallback(async () => { await Promise.all([load(), loadMeta()]); }, [load, loadMeta]);

  const removeSeatChange = async (id: string) => {
    try { await api.del(`/api/admin/kpis/seat-changes/${id}`); await load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not remove that seat change.'); }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <h1 className="page-title">KPIs</h1>
          {data && <span className="text-[13px] text-muted">As of {dayLabel(data.asOf)}</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setDialog('goals')} disabled={!data}>Edit goals</Button>
          <Button size="sm" onClick={() => setDialog('hours')} disabled={!data}>Log hours</Button>
          <Button size="sm" variant="primary" onClick={() => setDialog('seat')} disabled={!data}><Plus aria-hidden size={16} strokeWidth={2} />Seat change</Button>
        </div>
      </div>
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      {!data && !error && <Spinner label="Loading KPIs" />}
      {data && (
        <div className="flex flex-col gap-4">
          <Goals k={data} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Panel id="seats-h" title="Paid seats vs goal">
              <div className="px-4 pb-3 pt-2 sm:px-5"><SeatsChart seats={data.seats} goal={data.goals.paidSeats} today={data.asOf} /></div>
            </Panel>
            <Panel id="bugs-h" title="Bugs submitted per week" action={<Seg label="Weeks shown" value={weeks} onChange={setWeeks} options={[[12, '12 weeks'], [26, '6 months']]} />}>
              <div className="px-4 pb-3 pt-2 sm:px-5"><BugsChart weeks={data.bugs.weeks} /></div>
            </Panel>
          </div>
          <Also k={data} />
          <Funnel k={data} />
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
            <Panel id="firms-h" title="Onboarding hours by firm" className="lg:col-span-2"
              action={<Seg label="Hours window" value={window} onChange={setWindow} options={[['all', 'All time'], ['90', '90 days']]} />}>
              <FirmsTable k={data} />
            </Panel>
            <Panel id="changes-h" title="Recent seat changes">
              {data.recentSeatChanges.length === 0
                ? <p className="m-0 px-4 py-4 text-sm text-muted sm:px-5">No seat changes yet. Use Seat change when a firm starts or stops paying for seats.</p>
                : (
                  <ul className="m-0 list-none p-0">
                    {data.recentSeatChanges.map((c) => (
                      <li key={c.id} className="flex items-center gap-3 border-t border-line-soft px-4 py-2.5 text-sm first:border-t-0 sm:px-5">
                        <span className="w-14 flex-none text-muted">{short(c.on)}</span>
                        <span className="min-w-0 flex-1 truncate" title={c.note ?? undefined}>{c.firmName}</span>
                        <span className={cx('font-bold tabular-nums', c.change > 0 ? 'text-ok' : 'text-bad')}>{c.change > 0 ? `+${c.change}` : `−${-c.change}`}</span>
                        <button type="button" aria-label={`Remove ${c.change > 0 ? 'the addition of' : 'the removal of'} ${Math.abs(c.change)} seats for ${c.firmName} on ${fmtCalendarDate(c.on)}`}
                          onClick={() => void removeSeatChange(c.id)}
                          className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border-0 bg-transparent text-muted hover:bg-panel hover:text-bad">
                          <Trash2 aria-hidden size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
            </Panel>
          </div>
        </div>
      )}
      {data && (
        <>
          <SeatChangeDialog open={dialog === 'seat'} onClose={() => setDialog(null)} onSaved={saved} firms={meta} today={data.asOf} />
          <LogHoursDialog open={dialog === 'hours'} onClose={() => setDialog(null)} onSaved={saved} firms={meta} today={data.asOf} recent={data.recentTimeEntries} />
          <GoalsDialog open={dialog === 'goals'} onClose={() => setDialog(null)} onSaved={saved} goals={data.goals} today={data.asOf} />
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------- pieces

function Panel({ id, title, action, children, className }: { id: string; title: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section aria-labelledby={id} className={cx('min-w-0 overflow-hidden rounded-2xl border border-line bg-white shadow-card', className)}>
      <div className="flex min-h-[44px] items-center justify-between gap-3 border-b border-line-soft px-4 py-2 sm:px-5">
        <h2 id={id} className="card-title">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Seg<T extends string | number>({ label, value, onChange, options }: { label: string; value: T; onChange: (v: T) => void; options: Array<[T, string]> }) {
  return (
    <div role="group" aria-label={label} className="inline-flex gap-0.5 rounded-[10px] bg-subtle p-0.5">
      {options.map(([v, text]) => (
        <button key={String(v)} type="button" aria-pressed={value === v} onClick={() => onChange(v)}
          className={cx('min-h-8 whitespace-nowrap rounded-lg border-0 px-3 text-[13px]', value === v ? 'bg-white font-semibold text-ink shadow-seg' : 'bg-transparent font-medium text-ink-2 hover:text-ink')}>{text}</button>
      ))}
    </div>
  );
}

function Stat({ label, value, unit, context, pill, children, small }: {
  label: string; value: string; unit?: string; context: string; pill?: { tone: PillTone; text: string } | null; children?: ReactNode; small?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-2xl border border-line bg-white p-4 shadow-card sm:px-[18px]">
      <div className="section-label !text-xs !tracking-[0.08em]">{label}</div>
      <div className={cx('font-bold leading-[1.15] tracking-[-0.02em] tabular-nums', small ? 'text-[26px]' : 'text-[34px]')}>
        {value}{unit && <span className="ml-1.5 text-base font-semibold tracking-normal text-muted">{unit}</span>}
      </div>
      <div className="text-[13px] text-ink-2">{context}</div>
      {children}
      {pill && <Pill tone={pill.tone} className="mt-1.5">{pill.text}</Pill>}
    </div>
  );
}

const pillOf = (ok: boolean, good: string, bad: string): { tone: PillTone; text: string } => ({ tone: ok ? 'ok' : 'warn', text: ok ? good : bad });

// ---------------------------------------------------------------- the four goals

function Goals({ k }: { k: KpiResponse }) {
  const { goals, seats, cac, retention, bugs } = k;
  const g: KpiGoals = goals;
  const ahead = seats.aheadBy;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Stat label="Paid seats" value={String(seats.paid)} unit={g.paidSeats ? `of ${g.paidSeats.target}` : undefined}
        context={g.paidSeats ? `Goal: ${g.paidSeats.target} paid seats by ${fmtCalendarDate(g.paidSeats.targetOn)}` : 'No goal set. Use Edit goals to set one.'}
        pill={ahead === null ? null : ahead === 0 ? { tone: 'ok', text: 'On pace' } : pillOf(ahead > 0, `${ahead} ahead of pace`, `${-ahead} behind pace`)}>
        {g.paidSeats && <ProgressBar pct={Math.min(100, (seats.paid / g.paidSeats.target) * 100)} label={`${seats.paid} of ${g.paidSeats.target} paid seats`} height={8} className="mt-2" />}
      </Stat>
      <Stat label="Hours to onboard a seat" value={n1(cac.hoursPerSeat)} unit={cac.hoursPerSeat === null ? undefined : 'h per seat'}
        context={seats.paid === 0 ? 'No paid seats yet' : `${cac.onboardingHours} h logged across ${seats.paid} paid seats`}
        pill={g.hoursPerSeat && cac.hoursPerSeat !== null ? pillOf(cac.hoursPerSeat <= g.hoursPerSeat.target, `Under the ${g.hoursPerSeat.target} h goal`, `Over the ${g.hoursPerSeat.target} h goal`) : null} />
      <Stat label={`Seat retention · ${retention.windowDays} days`} value={retention.rate === null ? '—' : `${retention.rate}%`}
        context={retention.atStart === 0 ? `No seats were active ${retention.windowDays} days ago yet` : `${retention.kept} of the ${retention.atStart} seats active ${short(retention.since)} are still active`}
        pill={g.retention && retention.rate !== null ? pillOf(retention.rate >= g.retention.target, `Meets the ${g.retention.target}% goal`, `Goal ${g.retention.target}%`) : null} />
      <Stat label="Bugs submitted" value={String(bugs.lastWeek.count)} unit="last week"
        context={`${short(bugs.lastWeek.start)} – ${short(bugs.lastWeek.end)}${bugs.avgPerWeek === null ? '' : ` · ${bugs.avgPerWeek} a week over 8 weeks`}`}
        pill={bugs.changeVsPrior4 === null ? null
          : bugs.changeVsPrior4 === 0 ? { tone: 'neutral', text: 'Flat vs prior 4 weeks' }
          : pillOf(bugs.changeVsPrior4 < 0, `Down ${-bugs.changeVsPrior4}% vs prior 4 weeks`, `Up ${bugs.changeVsPrior4}% vs prior 4 weeks`)} />
    </div>
  );
}

function Also({ k }: { k: KpiResponse }) {
  const { bugs, goals, support, seats } = k;
  return (
    <section aria-labelledby="also-h">
      <h2 id="also-h" className="section-label mb-2.5 mt-1">Also tracking</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat small label="Bugs per paid seat" value={n1(bugs.perPaidSeat)}
          context={seats.paid === 0 ? 'No paid seats yet' : `${bugs.last4Weeks} bugs in 4 weeks across ${seats.paid} seats`} />
        <Stat small label="First reply to a bug" value={n1(bugs.firstReplyHoursMedian)} unit={bugs.firstReplyHoursMedian === null ? undefined : 'h median'}
          context="Last 30 days, from report to first answer"
          pill={goals.firstReplyHours && bugs.firstReplyHoursMedian !== null
            ? pillOf(bugs.firstReplyHoursMedian <= goals.firstReplyHours.target, `Under the ${goals.firstReplyHours.target} h goal`, `Over the ${goals.firstReplyHours.target} h goal`) : null} />
        <Stat small label="Time to fix a bug" value={n1(bugs.fixDaysMedian)} unit={bugs.fixDaysMedian === null ? undefined : 'days median'}
          context="Report to done, last 30 days"
          pill={bugs.blockingOpen > 0 ? { tone: 'warn', text: `${bugs.blockingOpen} blocking ${bugs.blockingOpen === 1 ? 'bug' : 'bugs'} open` } : { tone: 'ok', text: 'No blocking bugs open' }} />
        <Stat small label="Support hours per seat" value={n1(support.hoursPerSeat)} unit={support.hoursPerSeat === null ? undefined : 'h'}
          context={seats.paid === 0 ? 'No paid seats yet' : `Last 30 days after go-live · ${support.hours} h across ${seats.paid} seats`} />
      </div>
    </section>
  );
}

function Funnel({ k }: { k: KpiResponse }) {
  const { steps, stalled } = k.funnel;
  const top = Math.max(1, steps[0]?.firms ?? 0);
  const cols = 'grid grid-cols-[minmax(0,130px)_1fr_40px] items-center gap-3 sm:grid-cols-[170px_1fr_60px_100px_140px] sm:gap-3.5';
  return (
    <Panel id="funnel-h" title="Onboarding funnel" action={<Pill tone={stalled > 0 ? 'warn' : 'ok'}>{stalled > 0 ? `${stalled} ${stalled === 1 ? 'firm' : 'firms'} stalled` : 'No firms stalled'}</Pill>}>
      <div className="px-4 pb-3 sm:px-5">
        <div className={cx(cols, 'py-2 text-xs font-bold uppercase tracking-[0.06em] text-muted')}>
          <span>Step</span><span aria-hidden />
          <span className="text-right">Firms</span><span className="hidden text-right sm:block">Of invited</span><span className="hidden text-right sm:block">Median days in</span>
        </div>
        {steps.map((s, i) => (
          <div key={s.id} className={cx(cols, 'border-t border-line-soft py-2 text-sm')}>
            <span className="truncate">{s.label}</span>
            <ProgressBar pct={(s.firms / top) * 100} label={`${s.firms} of ${top} firms`} height={18} className={i === steps.length - 1 ? undefined : '[&>span]:bg-brand-ring'} />
            <span className="text-right tabular-nums">{s.firms}</span>
            <span className="hidden text-right tabular-nums sm:block">{Math.round((s.firms / top) * 100)}%</span>
            <span className="hidden text-right tabular-nums sm:block">{s.medianDaysToNext === null ? '·' : s.medianDaysToNext}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function FirmsTable({ k }: { k: KpiResponse }) {
  if (k.firms.length === 0) return <p className="m-0 px-4 py-4 text-sm text-muted sm:px-5">No hours or seats yet. Use Log hours and Seat change to add them.</p>;
  return (
    <TableBox bare minWidth={520} label="Onboarding hours table">
      <thead>
        <tr className="border-b border-line-soft">
          <Th>Firm</Th><Th className="text-right">Paid seats</Th><Th className="text-right">Hours</Th><Th className="text-right">Hours / seat</Th><Th>Lead</Th>
        </tr>
      </thead>
      <tbody>
        {k.firms.map((f) => (
          <tr key={f.firmId} className="border-t border-line-soft first:border-t-0">
            <Td className="font-medium">{f.name}</Td>
            <Td className="text-right tabular-nums">{f.paidSeats}</Td>
            <Td className="text-right tabular-nums">{f.onboardingHours}</Td>
            <Td className="text-right tabular-nums">{n1(f.hoursPerSeat)}</Td>
            <Td>{f.lead ? firstName(f.lead) : '—'}</Td>
          </tr>
        ))}
      </tbody>
    </TableBox>
  );
}
